// Revisado: 2025-12-18 [I18n Scan]
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from '../../../../constants';
import { createPlaceholderUrl } from '../../../../utils/PlaceholderCreator';

// --- INLINE SVGs for Effects ---
const StarIcon = ({ size, className, fill, style }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size} height={size}
    viewBox="0 0 24 24"
    fill={fill || "none"}
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    style={style}
  >
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

const SparklesIcon = ({ className, style }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    stroke="none"
    className={className}
    style={style}
  >
    <path d="M9.9 2.5L11 6.3L14.7 7.4L11 8.5L9.9 12.3L8.8 8.5L5.1 7.4L8.8 6.3L9.9 2.5ZM17.6 12.5L18.5 15.3L21.3 16.2L18.5 17.1L17.6 19.9L16.7 17.1L13.9 16.2L16.7 15.3L17.6 12.5ZM5.6 14.5L6.5 17.3L9.3 18.2L6.5 19.1L5.6 21.9L4.7 19.1L1.9 18.2L4.7 17.3L5.6 14.5Z" />
  </svg>
);

// --- CONSTELACIÓN FIJA (DETERMINISTA) ---
// Reglas de Distribución Estrictas:
// 1. Horizontal:
//    - Izquierda: 0% - 35% (Permitido)
//    - Centro: 35% - 65%   (PROBIBIDO - 30% ancho reservado)
//    - Derecha: 65% - 100% (Permitido)
// 2. Vertical:
//    - Superior: 0% - 80%  (Permitido)
//    - Inferior: 80% - 100% (PROBIBIDO - 20% alto reservado para pies/sombra)

const FIXED_CONSTELLATION = [
  // --- ZONA IZQUIERDA (0-35% Left, 0-80% Top) ---
  { top: '5%', left: '5%', size: 16, rotation: 15, opacity: 0.35, delay: '0s' },
  { top: '15%', left: '15%', size: 12, rotation: -10, opacity: 0.25, delay: '1.2s' },
  { top: '8%', left: '28%', size: 14, rotation: 45, opacity: 0.2, delay: '0.5s' },
  { top: '25%', left: '8%', size: 18, rotation: -30, opacity: 0.15, delay: '2.1s' },
  { top: '35%', left: '25%', size: 10, rotation: 60, opacity: 0.3, delay: '1.8s' },
  { top: '42%', left: '5%', size: 14, rotation: 10, opacity: 0.2, delay: '0.8s' },
  { top: '48%', left: '28%', size: 11, rotation: -15, opacity: 0.25, delay: '2.4s' },
  { top: '55%', left: '12%', size: 16, rotation: 20, opacity: 0.2, delay: '1.0s' },
  { top: '65%', left: '6%', size: 13, rotation: -45, opacity: 0.15, delay: '1.5s' },
  { top: '72%', left: '25%', size: 15, rotation: 5, opacity: 0.25, delay: '0.3s' },
  { top: '60%', left: '32%', size: 9, rotation: 30, opacity: 0.15, delay: '2.8s' }, // Borde interno
  { top: '78%', left: '10%', size: 12, rotation: -10, opacity: 0.3, delay: '1.9s' }, // Límite inferior

  // --- ZONA DERECHA (65-100% Left, 0-80% Top) ---
  { top: '6%', left: '92%', size: 18, rotation: -20, opacity: 0.3, delay: '0.2s' },
  { top: '12%', left: '70%', size: 13, rotation: 30, opacity: 0.25, delay: '1.7s' },
  { top: '18%', left: '85%', size: 15, rotation: 12, opacity: 0.2, delay: '1.1s' },
  { top: '28%', left: '95%', size: 11, rotation: -45, opacity: 0.35, delay: '2.3s' },
  { top: '32%', left: '72%', size: 17, rotation: 5, opacity: 0.15, delay: '0.4s' },
  { top: '40%', left: '88%', size: 14, rotation: 25, opacity: 0.25, delay: '1.6s' },
  { top: '48%', left: '68%', size: 10, rotation: -15, opacity: 0.2, delay: '2.2s' },
  { top: '55%', left: '90%', size: 16, rotation: 40, opacity: 0.15, delay: '0.9s' },
  { top: '62%', left: '78%', size: 12, rotation: -10, opacity: 0.3, delay: '1.4s' },
  { top: '70%', left: '94%', size: 14, rotation: 15, opacity: 0.2, delay: '2.6s' },
  { top: '75%', left: '70%', size: 19, rotation: -5, opacity: 0.15, delay: '0.7s' },
  { top: '5%', left: '68%', size: 9, rotation: 0, opacity: 0.2, delay: '3.0s' }, // Arriba borde

  // --- RELLENO EXTRA ZONA SUPERIOR (0-20% Top, Todo ancho excepto centro prohibido) ---
  // Aceptamos estrellas "un poco" más cerca del centro SOLO arriba del todo
  { top: '2%', left: '40%', size: 8, rotation: 0, opacity: 0.15, delay: '2.5s' }, // Cerca cuello izq
  { top: '2%', left: '60%', size: 8, rotation: 0, opacity: 0.15, delay: '2.0s' }, // Cerca cuello der
];

// --- CONSTANT PLACEHOLDER ---
const PLACEHOLDER_URL = createPlaceholderUrl(800, 1067, 'Traje Original', '#E0E0E0', '#AAAAAA');


// --- DYNAMIC EFFECTS COMPONENT ---
const DynamicEffects = ({ level }) => {
  return (
    <>
      {/* --- FONDO: PATRÓN DE ESTRELLAS (Solo Nivel 5) --- */}
      {level === 5 && (
        <div className="absolute inset-0 z-0 pointer-events-none">
          {FIXED_CONSTELLATION.map((star, i) => (
            <StarIcon
              key={i}
              size={star.size}
              className="absolute text-yellow-300 animate-pulse"
              style={{
                top: star.top,
                left: star.left,
                transform: `rotate(${star.rotation}deg)`,
                opacity: star.opacity,
                animationDelay: star.delay,
                animationDuration: '3s'
              }}
              fill="currentColor"
            />
          ))}
        </div>
      )}

      {/* --- PRIMER PLANO: INDICADOR DE ESTRELLAS (Esquina Inferior Derecha) --- */}
      <div className="absolute bottom-6 right-6 z-30 pointer-events-none">
        {/* Contenedor invisible para la constelación */}
        <div className="relative w-20 h-20 transition-transform duration-300">

          {/* ESTRELLA 1: Base */}
          {level >= 1 && (
            <StarIcon size={18} className="absolute bottom-2 left-4 text-yellow-400 drop-shadow-md animate-in zoom-in spin-in-12 duration-300 rotate-12" fill="currentColor" />
          )}

          {/* ESTRELLA 2: Satélite */}
          {level >= 2 && (
            <StarIcon size={12} className="absolute top-1 right-3 text-yellow-300 drop-shadow-md animate-in zoom-in spin-in-45 duration-300 delay-75 -rotate-12" fill="currentColor" />
          )}

          {/* ESTRELLA 3: Cuerpo Principal */}
          {level >= 3 && (
            <StarIcon size={22} className="absolute top-6 left-0 text-yellow-400 drop-shadow-lg animate-in zoom-in spin-in-[-12deg] duration-300 delay-100 rotate-6" fill="currentColor" />
          )}

          {/* ESTRELLA 4: Ancla Derecha */}
          {level >= 4 && (
            <StarIcon size={15} className="absolute bottom-5 right-0 text-yellow-400 drop-shadow-md animate-in zoom-in spin-in-12 duration-300 delay-150 -rotate-45" fill="currentColor" />
          )}

          {/* ESTRELLA 5: CENTRO (La Madre) */}
          {level === 5 && (
            <div className="absolute inset-0 flex items-center justify-center animate-in zoom-in duration-500">
              <div className="absolute w-14 h-14 bg-yellow-500/30 rounded-full blur-xl animate-pulse"></div>
              <StarIcon size={36} className="text-yellow-300 drop-shadow-[0_0_15px_rgba(253,224,71,1)] relative z-20 animate-bounce-slow" fill="currentColor" />
              <SparklesIcon className="absolute w-44 h-44 text-yellow-100 opacity-20 animate-spin-slow z-0" style={{ animationDuration: '8s' }} />
            </div>
          )}
        </div>
      </div>
    </>
  );
};


const CharacterImage = ({ character, costume, selectedStars, onStarsChange, initialStars }) => {
  const { t } = useTranslation();
  const [isLoaded, setIsLoaded] = useState(false);
  const [currentThumbUrl, setCurrentThumbUrl] = useState(null);




  // --- 1. DETECCIÓN DE DESINCRONIZACIÓN (SOLUCIÓN REAL) ---
  // Detectamos si estamos en ese "limbo" donde el personaje cambió pero el traje no.
  // Si hay mismatch, forzamos a 'effectiveCostume' a ser null para no mostrar datos erróneos.
  // Note: Both objects use 'Id_Personaje' based on OriginalDisplay.js logic
  const isDataMismatch = costume && character && costume.Id_Personaje !== character.Id_Personaje;
  const effectiveCostume = isDataMismatch ? null : costume;

  // Usamos effectiveCostume para los textos. Si estamos desincronizados, mostramos texto de carga o fallback.
  const outfitName = effectiveCostume
    ? effectiveCostume.title_en
    : (character ? t('CharacterImage.fallback_default_outfit') : t('CharacterImage.fallback_select_character'));

  const activeStars = selectedStars !== null ? selectedStars : initialStars || 1;

  const sanitizeFilename = (name) => {
    if (typeof name !== 'string') return '';
    return name.replace(/[\\/*?:"<>|]/g, '');
  };

  // --- 2. USEEFFECT OPTIMIZADO ---
  useEffect(() => {
    // Si no hay personaje o estamos en un estado desincronizado (Mismatch),
    // NO cargamos nada nuevo y ocultamos la imagen actual suavemente.
    if (!character || isDataMismatch) {
      setIsLoaded(false); // Esto dispara el fade-out (opacity: 0)
      // NO establecemos currentThumbUrl a placeholder aquí para evitar el "flash" gris.
      // Simplemente esperamos a que el estado se sincronice.
      return;
    }

    if (effectiveCostume) { // Usamos effectiveCostume, que ya está sanitizado
      try {
        const characterName = sanitizeFilename(character.name_en).replace(/ /g, '_');
        const reference = sanitizeFilename(effectiveCostume.reference).replace(/ /g, '_');
        const costumeName = sanitizeFilename(effectiveCostume.title_en).replace(/ /g, '_');
        const imageName = `${characterName}_${reference}_${costumeName}.png`;

        const originalPath = `/images/character/${imageName}`;
        const finalUrl = `${API_BASE_URL}/api/thumb?img=${originalPath}&width=512`;

        setCurrentThumbUrl(finalUrl);
        // Nota: No ponemos setIsLoaded(true) aquí. 
        // Esperamos al evento onLoad de la etiqueta <img> para hacer el fade-in real.
        setIsLoaded(false);
      } catch (error) {
        console.error("URL Construction Error:", error);
        setCurrentThumbUrl(PLACEHOLDER_URL);
      }
    } else {
      // Caso: Personaje seleccionado pero sin traje específico (o fallback)
      setCurrentThumbUrl(PLACEHOLDER_URL);
      setIsLoaded(false);
    }
  }, [character, costume, isDataMismatch, effectiveCostume]);

  return (
    <div className="relative bg-white dark:bg-slate-800 rounded-3xl shadow-2xl dark:shadow-black/50 p-2 w-full max-w-[512px] mx-auto transition-all duration-300 group ring-1 ring-gray-100 dark:ring-slate-700/50">

      {/* --- STAR SELECTOR (Top Right) --- */}
      <div className="absolute top-6 right-6 flex gap-1 bg-black/20 backdrop-blur-md p-1.5 px-3 rounded-full z-40 border border-white/20 shadow-lg transition-transform duration-300 group-hover:scale-105">
        {[1, 2, 3, 4, 5].map((value) => (
          <button
            key={value}
            onClick={() => onStarsChange(value)}
            className="focus:outline-none transition-transform active:scale-90 hover:scale-125"
          >
            <StarIcon
              size={18}
              className={`transition-colors duration-300 ${value <= activeStars
                ? 'text-yellow-400 fill-yellow-400 drop-shadow-[0_0_8px_rgba(250,204,21,0.6)]'
                : 'text-white/40 fill-transparent'
                }`}
            />
          </button>
        ))}
      </div>

      {/* --- IMAGE + OVERLAY --- */}
      <div className="relative aspect-square w-full rounded-2xl overflow-hidden bg-gray-50 dark:bg-slate-900 border border-gray-100 dark:border-slate-700/50 transition-colors duration-300">
        {/* Background Gradients */}
        <div className="absolute inset-0 z-0 pointer-events-none opacity-80" style={{ background: 'linear-gradient(to bottom, rgb(245, 190, 177), transparent)' }}></div>
        <div className="absolute inset-0 opacity-[0.05] dark:opacity-[0.1] pointer-events-none z-0" style={{ backgroundImage: 'radial-gradient(circle, currentColor 1px, transparent 1px)', backgroundSize: '20px 20px' }}></div>

        <DynamicEffects level={activeStars} />

        <img
          key={currentThumbUrl}
          src={currentThumbUrl || PLACEHOLDER_URL}
          alt={costume?.title_en || t('CharacterImage.alt_character')}
          className="relative z-10 w-full h-full object-contain object-bottom p-1 transition-all duration-[1000ms] ease-out"
          style={{
            clipPath: isLoaded ? 'inset(0 0 0 0)' : 'inset(0 0 100% 0)',
            opacity: isLoaded ? 1 : 0
          }}
          onLoad={() => setIsLoaded(true)}
          draggable="false"
          onError={(e) => { e.target.src = PLACEHOLDER_URL; setIsLoaded(true); }}
        />

        {/* Bottom Gradient Overlay */}
        <div className="absolute bottom-0 left-0 right-0 h-1/2 pointer-events-none z-20" style={{ background: 'linear-gradient(to top, rgba(223, 94, 145, 0.85), transparent)' }}></div>

        {/* Text Overlay */}
        <div className="absolute bottom-0 left-0 w-full p-6 z-30 flex flex-col justify-end items-start animate-in slide-in-from-bottom-4 duration-500 fade-in">
          <div className="flex items-center gap-1.5 mb-1 opacity-90">
            <div className={`w-8 h-1 rounded-full shadow-sm transition-colors duration-500 ${activeStars >= 5 ? 'bg-yellow-300 shadow-yellow-300' : 'bg-white/80'}`}></div>
            <span className={`text-[10px] font-bold uppercase tracking-[0.2em] drop-shadow-md transition-colors duration-500 ${activeStars >= 5 ? 'text-yellow-300' : 'text-white'}`}>
              {t('CharacterImage.label_outfit_model')}
            </span>
          </div>
          <h2 className="text-2xl sm:text-3xl font-black text-white leading-none tracking-tight drop-shadow-md uppercase italic truncate w-full">
            {outfitName}
          </h2>
        </div>
      </div>
    </div>
  );
};

export default CharacterImage;
// Revisado: 2025-12-16